/**
 * Invoice Summary Contract
 * 
 * Single source of truth for Live Preview.
 * All calculations happen here, preview is purely presentational.
 */

/**
 * Calculate invoice summary from invoice data
 * @param {Object} invoice - Raw invoice data
 * @param {Object} companySettings - Company settings for state comparison
 * @returns {Object} invoiceSummary - Derived summary for preview
 */
export function calculateInvoiceSummary(invoice, companySettings = null) {
  const items = invoice.items || [];

  // Calculate subtotal
  const subtotal = items.reduce((sum, item) => {
    const base = (item.quantity || 0) * (item.rate || 0);
    const discount =
      item.discountType === "percentage"
        ? (base * (item.discount || 0)) / 100
        : item.discount || 0;
    return sum + Math.max(0, base - discount);
  }, 0);

  // Calculate tax
  const companyState = companySettings?.state || "Maharashtra";
  const clientState = invoice.client?.state || "Maharashtra";
  
  // Normalize state names for comparison (case-insensitive, trim whitespace)
  const normalizeState = (state) => state?.toLowerCase().trim() || "";
  const sameState = normalizeState(companyState) === normalizeState(clientState);
  
  console.log('Tax calculation:', {
    companyState,
    clientState,
    sameState,
    normalizedCompany: normalizeState(companyState),
    normalizedClient: normalizeState(clientState)
  });

  let cgst = 0, sgst = 0, igst = 0;

  items.forEach((item) => {
    const amount = Math.max(
      0,
      (item.quantity || 0) * (item.rate || 0) -
        ((item.discountType === "percentage"
          ? ((item.quantity || 0) * (item.rate || 0) * (item.discount || 0)) / 100
          : item.discount) || 0)
    );

    const tax = (amount * (item.gstRate || item.tax || 0)) / 100;

    if (sameState) {
      cgst += tax / 2;
      sgst += tax / 2;
    } else {
      igst += tax;
    }
  });

  const taxTotal = cgst + sgst + igst;

  // Calculate adjustments
  const discountType = invoice.discountType || "flat";
  const discountValue = invoice.discount || 0;
  const discountAmount =
    discountType === "percentage"
      ? (subtotal * discountValue) / 100
      : discountValue;

  const shipping = invoice.shipping || 0;
  const rounding = invoice.roundOff || 0;
  const adjustments = -discountAmount + shipping + rounding;

  // Calculate totals
  const total = subtotal + taxTotal + adjustments;
  const paidAmount = invoice.paidAmount || 0;
  const balanceDue = total - paidAmount;

  // Determine payment status
  let paymentStatus = "unpaid";
  if (paidAmount >= total && total > 0) {
    paymentStatus = "paid";
  } else if (paidAmount > 0) {
    paymentStatus = "partially_paid";
  }

  // Extract location from billing address
  const extractLocation = (address) => {
    if (!address) return null;
    const lines = address.split('\n').filter(line => line.trim());
    const lastLine = lines[lines.length - 1];
    if (!lastLine) return null;
    const match = lastLine.match(/^([^,]+),\s*([A-Za-z\s]+)/);
    if (match) return `${match[1]}, ${match[2]}`;
    return lastLine;
  };

  // Build summary contract
  return {
    // Invoice meta
    invoiceNumber: invoice.invoiceNumber || "INV-0001",
    issueDate: invoice.issueDate,
    status: invoice.status || "draft",
    
    // Bill to
    billTo: invoice.client ? {
      name: invoice.client.name,
      location: extractLocation(invoice.billingAddress),
    } : null,
    
    // Items (simplified for preview)
    items: items.map(item => {
      const qty = Number(item.quantity) || 0;
      const rate = Number(item.rate) || 0;
      const base = qty * rate;
      const discount =
        item.discountType === "percentage"
          ? (base * (Number(item.discount) || 0)) / 100
          : Number(item.discount) || 0;
      const amount = Math.max(0, base - discount);

      // Metadata
      const metadata = [];
      if (item.type === "product" && item.hsn) metadata.push(`HSN ${item.hsn}`);
      if (item.type === "service" && item.sac) metadata.push(`SAC ${item.sac}`);
      if (item.uom) metadata.push(item.uom);

      return {
        name: item.name || "Untitled Item",
        quantity: qty,
        rate: rate,
        amount: amount,
        metadata: metadata.join(" • ") || null,
      };
    }),
    
    // Totals
    subtotal,
    taxTotal,
    adjustments: adjustments !== 0 ? adjustments : null,
    total,
    
    // Payment
    paidAmount,
    balanceDue,
    paymentStatus,
    
    // Tax breakdown (for full invoice view)
    taxBreakdown: {
      cgst,
      sgst,
      igst,
      sameState,
    },
    
    // Flags
    hasItems: items.length > 0,
    hasClient: !!invoice.client,
    hasAdjustments: adjustments !== 0,
    hasPayments: paidAmount > 0,
  };
}

/**
 * Validate invoice summary contract
 * Ensures all required fields are present
 */
export function validateInvoiceSummary(summary) {
  const required = [
    'invoiceNumber',
    'status',
    'items',
    'subtotal',
    'taxTotal',
    'total',
    'balanceDue',
    'paymentStatus',
  ];

  const missing = required.filter(field => summary[field] === undefined);
  
  if (missing.length > 0) {
    console.error('Invalid invoice summary contract. Missing fields:', missing);
    return false;
  }

  return true;
}
